import type { Tables } from '../../db_types'
import {
  compileDataViewFilters,
  type DataViewCompileOptions,
  type DataViewSource
} from '../data_views'
import type { SourceGridFilterExpression } from '../source-grid'

export type MetricPreviewDatabasePredicate = {
  field: string
  value: string | number | boolean
}

/**
 * Extract only predicates implied by the Metric filter and equivalent to a
 * native database equality. Unsupported filters stay in the live evaluator.
 */
export const getMetricPreviewDatabasePredicates = (
  metric: Tables<'metrics'>,
  options: DataViewCompileOptions
): MetricPreviewDatabasePredicate[] => {
  if (!metric.source || !['deal', 'activity', 'invoice', 'kpi', 'custom_record'].includes(metric.source) ||
      !metric.filters || typeof metric.filters !== 'object' || Array.isArray(metric.filters)) return []

  let expression: SourceGridFilterExpression
  try {
    expression = compileDataViewFilters(
      metric.filters as Record<string, unknown>, metric.source as DataViewSource, options
    ).expression
  } catch {
    return []
  }

  const collect = (node: SourceGridFilterExpression): MetricPreviewDatabasePredicate[] => {
    if (node.kind === 'group') {
      return node.operator === 'and' ? node.filters.flatMap(collect) : []
    }
    const { field, operand } = node
    if (node.operator !== 'eq' || field.kind !== 'native' || field.sourceRole !== 'local' ||
        operand.kind !== 'literal' || operand.value == null ||
        typeof operand.value === 'object') return []
    if (field.valueType === 'number' && typeof operand.value === 'number') {
      return [{ field: field.field, value: operand.value }]
    }
    if (field.valueType === 'boolean' && typeof operand.value === 'boolean') {
      return [{ field: field.field, value: operand.value }]
    }
    // The Metric evaluator coerces numeric-looking text to numbers. Do not
    // narrow text such as "0012" to a database equality with "12".
    if (field.valueType === 'text' && typeof operand.value === 'string' &&
        operand.value !== '' && Number.isNaN(Number(operand.value)) &&
        operand.value !== 'true' && operand.value !== 'false') {
      return [{ field: field.field, value: operand.value }]
    }
    return []
  }

  return collect(expression)
}
